import React, { ElementRef, useRef } from 'react';
import styled from 'styled-components';
import CubeIcon from '@svg/cube';

import { Label } from '@common/Label';
import { alpha, Stack, TextField } from '@mui/material';
import { AutoAwesomeOutlined, RingVolumeOutlined, SwitchAccountOutlined, MessageOutlined } from '@mui/icons-material';
import { TemplateType } from '@requests/templates';

const Container = styled.div`
  .name {
    display: flex;
    flex-direction: column;
    gap: 8px;

    border-bottom: 1px solid ${({ theme }) => theme.line.light};
    padding: 24px 32px;
  }
`;

const TemplateCategories = styled.div`
  display: flex;
  padding: 24px 32px;
  flex-direction: column;
  gap: 8px;
`;

const CategoryTab = styled.div<{ active: boolean; disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 6px;
  padding: 10px 16px;
  gap: 8px;

  background-color: ${({ theme, active }) => (active ? alpha(theme.primary, 0.12) : undefined)};
  border-color: ${({ theme, active }) => (active ? theme.primary : undefined)};

  p {
    color: ${({ theme, active, disabled }) =>
      disabled ? alpha(theme.text.standard, 0.15) : active ? theme.primary : undefined};

    font-size: 14px;
    margin: 0;
  }

  #icon {
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      font-size: 16px;
      color: ${({ theme, active, disabled }) =>
        disabled ? alpha(theme.text.standard, 0.15) : active ? theme.primary : undefined};
    }
  }

  &:hover {
    cursor: ${({ disabled }) => (disabled ? undefined : 'pointer')};
    background-color: ${({ theme, disabled }) => (disabled ? undefined : alpha(theme.primary, 0.12))};
    border-color: ${({ theme, disabled }) => (disabled ? undefined : theme.primary)};
    color: ${({ theme, disabled }) => (disabled ? undefined : theme.primary)};

    svg {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

interface Props {
  name: string;
  selectedTemplate: string | null;
  setName: React.Dispatch<string>;
  category: TemplateType;
  setCategory: React.Dispatch<TemplateType>;
}

export const SelectTemplateSidebar: React.FC<Props> = ({ name, setName, selectedTemplate, category, setCategory }) => {
  const textBoxRef = useRef<ElementRef<typeof TextField>>(null);
  const isNameError = Boolean((name.length < 3 || name.length > 64 || name.length === 0) && name);
  const isInputError = Boolean(isNameError || (selectedTemplate && !name));

  const categories = [
    { id: TemplateType.CHATBOT, label: 'Chatbots', icon: <AutoAwesomeOutlined /> },
    { id: TemplateType.MODULE, label: 'Modules', icon: <CubeIcon height={16} /> },
    { id: TemplateType.CALL_CONTROL, label: 'Call Control', icon: <RingVolumeOutlined /> },
    { id: TemplateType.MESSAGING, label: 'Messaging', icon: <MessageOutlined /> },
    { id: TemplateType.USER_DEFINED, label: 'User Templates', icon: <SwitchAccountOutlined /> },
  ];

  return (
    <Container>
      <div className='name'>
        <Label title='Workflow Name' tooltip={`A name for your workflow.`} />
        <TextField
          size='small'
          fullWidth
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          ref={textBoxRef}
          value={name}
          error={isInputError}
          helperText={
            isNameError ? `Must be ${name.length < 3 ? 'at least 3 characters' : 'less than 64 characters'}` : undefined
          }
          required
          placeholder={`A name for your workflow`}
          onChange={(e) => {
            setName(e.target.value);
          }}
          data-intercom-target='workflow-name-input-field'
        />
      </div>
      <TemplateCategories>
        <Label title='Template Categories' tooltip='View different categorised templates.' />
        {categories.map((categoryTab) => (
          <CategoryTab
            key={categoryTab.id}
            active={category === categoryTab.id}
            onClick={() => setCategory(categoryTab.id)}
          >
            <Stack direction='row' alignItems='center' spacing={1}>
              <div id='icon'>{categoryTab.icon}</div>
              <p>{categoryTab.label}</p>
            </Stack>
          </CategoryTab>
        ))}
      </TemplateCategories>
    </Container>
  );
};
